import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useReview } from "../hooks/useReview";
import { setSessionToken } from "../lib/api";
import VideoPlayer from "../components/VideoPlayer";
import FormatTabs from "../components/FormatTabs";
import FeedbackForm from "../components/FeedbackForm";
import DecisionButtons from "../components/DecisionButtons";
import Countdown from "../components/Countdown";

export default function Review() {
  const { token } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessionKey, setSessionKey] = useState(0);

  // Capture session token from URL (set by auth redirect)
  useEffect(() => {
    const session = searchParams.get("session");
    if (session) {
      setSessionToken(session);
      // Remove session from URL to clean it up
      searchParams.delete("session");
      setSearchParams(searchParams, { replace: true });
      // Trigger re-fetch with new session
      setSessionKey((k) => k + 1);
    }
  }, [searchParams, setSearchParams]);

  // sessionKey forces useReview to re-run when session is captured
  const { state, submit } = useReview(token || "", sessionKey);

  const [activeFormat, setActiveFormat] = useState<"16x9" | "9x16">("16x9");
  const [feedback, setFeedback] = useState("");

  if (state.status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-400">Loading review...</div>
      </div>
    );
  }

  if (state.status === "error") {
    if (state.error === "EXPIRED") {
      navigate("/expired", { replace: true });
      return null;
    }
    if (state.error === "NOT_FOUND") {
      navigate("/404", { replace: true });
      return null;
    }
    if (state.error === "UNAUTHORIZED") {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen px-4">
          <div className="text-center">
            <div className="text-6xl mb-4">&#128274;</div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Authentication Required
            </h1>
            <p className="text-gray-400 max-w-md">
              Please use the magic link from your email to access this review.
            </p>
          </div>
        </div>
      );
    }
    if (state.error === "FORBIDDEN") {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen px-4">
          <div className="text-center">
            <div className="text-6xl mb-4">&#128683;</div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Access Denied
            </h1>
            <p className="text-gray-400 max-w-md">
              You are not authorized to view this review.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-red-400">
          Something went wrong. Please try again later.
        </div>
      </div>
    );
  }

  if (state.status === "submitted") {
    navigate("/done", {
      replace: true,
      state: { decision: state.decision, message: state.message },
    });
    return null;
  }

  const review = state.review;
  const isSubmitting = state.status === "submitting";

  const has16x9 = "16x9" in review.preview_urls;
  const has9x16 = "9x16" in review.preview_urls;
  const effectiveFormat = activeFormat === "9x16" && has9x16 ? "9x16" : "16x9";
  const videoUrl = review.preview_urls[effectiveFormat] || "";

  const handleSubmit = async (decision: "approved" | "rejected") => {
    try {
      await submit(decision, feedback || undefined);
    } catch {
      alert("Failed to submit. Please try again.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <header className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-white mb-2">
          {review.episode_id}
        </h1>
        <Countdown expiresAt={review.deadline_at} />
        {review.notes && (
          <p className="text-gray-400 mt-2 text-sm max-w-2xl mx-auto">
            {review.notes}
          </p>
        )}
      </header>

      {has16x9 && has9x16 && (
        <FormatTabs active={activeFormat} onChange={setActiveFormat} />
      )}

      {videoUrl ? (
        <VideoPlayer
          src={videoUrl}
          aspectRatio={effectiveFormat === "16x9" ? "16/9" : "9/16"}
        />
      ) : (
        <div className="aspect-video max-w-4xl mx-auto bg-gray-800 rounded-lg flex items-center justify-center">
          <p className="text-gray-500">No preview available</p>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        <FeedbackForm
          value={feedback}
          onChange={setFeedback}
          disabled={isSubmitting}
        />

        <DecisionButtons
          onApprove={() => handleSubmit("approved")}
          onReject={() => handleSubmit("rejected")}
          disabled={isSubmitting}
        />

        {isSubmitting && (
          <div className="text-center mt-4 text-gray-400">Submitting...</div>
        )}
      </div>
    </div>
  );
}
